// ==============================================================================================
//  APPLICATION AUTHORSHIP & RIGHTS NOTICE
//
//  Developed by: Gareth Morgan
//  Brand & Product: BuildMyChecks.co.uk
//  Ownership: Sole author and rights holder
//
//  ----------------------------------------------------------------------------------------------
//  NOTICE:
//  - This application and its related systems were designed, developed, and built solely
//    by Gareth Morgan.
//  - Gareth Morgan retains full authorship, intellectual property, and creative rights to
//    all Build My Checks branding, technology, source code, architecture, and design.
//  - No ownership, license, or implied rights are granted to any third party unless
//    explicitly agreed in writing by Gareth Morgan.
//  - Gareth Morgan is NOT responsible for future maintenance, updates, security patching,
//    or operational upkeep if this application is deployed, copied, or modified without
//    his written consent.
//  - Gareth Morgan reserves the right to:
//       • Withdraw or disable access to the application or related services
//       • Remove hosted, cloud-based, or linked services under his control
//       • Revoke access to Build My Checks–related assets, code, APIs, or integrations
//
//  © 2025 Gareth Morgan — BuildMyChecks.co.uk
//  Unauthorized use, alteration, copying, or redistribution without written consent
//  is strictly prohibited.
//
//  ----------------------------------------------------------------------------------------------
//  Build My Checks – ASCII Logo Signature
//  (Symbolic representation of BuildMyChecks.co.uk identity)
//
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................:-==++-.........:-:.............................................
// ...................................-*******:.......=*****+-.........................................
// ...................................=*******+:.....+*******=.........................................
// .........................::........+**********************=.........................................
// .......................-+***+-.:-++***********************=:.......:................................
// .....................-+*************************************+-..=+**+-..............................
// .....................:+*************+=-.............-++***************+:............................
// ......................:+********+=:.....................:=*************=............................
// .......................=******+:...........................-+********+-.............................
// ......................-*****+:...............................-+*****+-..............................
// ................:::::+****+-...................................=*****+..............................
// ...............+-......:=+-........+=.*%=......##:.:::.....:::..-*****=.............................
// ..............=*-*@%%%%@*.........-#*:#@=......@%::@@@*...*@@#...-+++++**++=-.......................
// .............:**-*@*..=@%.=#+..*#=-%#:#@=.=#%#+@%::@%%%-.=@%@#-##:..##:+*****:......................
// ..............-+-*@@%%@%+.+@*..#@==%#:#@==@%-:+@%::@#-@#:%#+%#.+@#.*@+-******=......................
// .................*@*..:#@++@*..#@==%#:#@=+@#...@%::@#.+@%%-=%#..%@#%#:+******+......................
// .................*@%##%@#.-%@%%@@==%#:#@=:#@%#%%%::@#.:#@*.=%#...%@%:=****+-:.......................
// ..................::........::.........:....::.....:....:......:-#@--***+:..........................
// ................:++:+@@@@@*.:@#:.....................#%=......:#%#-.+***+:..........................
// .............-+**+:#@*:.:+%+:@#-=+-...:=+=-....-+=-..#%=.:=-..-++-..+***+:..........................
// .............+***=-@%.......:@@#+#@#.+@#-+%#:+@%++@%:#%+*@#::%#--*-:*****+-.........................
// .............-***=:@%.......:@%:.-%%:%@%%%%%+#@=.....#@@@%:..#%@@%-=**+++++*+.......................
// ..............****-+%@*=+%@*:@%:.-%%.*@*-=*=.*@%==%#:#@+=@%=:++-+@*-+--===:+=.......................
// .................::::=***=:..++..:++..-+**=...:+**+:.=+-.:++:-*#*=::-:+***=::-++:...................
// ....................-****+=............................*#......+*****************=..................
// .....................=*****=.......+##+.:+##=....*+.-#-*#.**:..:+************++*=...................
// .....................+******+-....*%..:.%*.:%+..:%*.=%=*%%*..:--***********+:..-+:..................
// ....................+*********=:%=+%*+%-*%+*%-=#-#%*%@=*#:%*:+****+++****+=...=*****-...............
// ...................=**********+--::.::....::...:..::.:.:-::-:=***+:..=+*+:..:+******-...............
// ....................-******++*******+=-:............-=++****+=+***+:...:...=******+-:...............
// ......................-++-....=+***************************+=+-=****+:...-+******-..................
// ................................=**********************++-:....=******=:+********=..................
// ................................=**********************=......:+*****************+:.................
// ................................+*******=......+*******=........-+=.:=+****+-.-+-...................
// ................................:-=+***-........+******=..............=***+.........................
// .................................................--::...............................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
// ....................................................................................................
//
// ==============================================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../firebase';
import ProgressStepper from '../../components/ProgressStepper';

export default function CompanySetup() {
  const navigate = useNavigate();
  const [companyName, setCompanyName] = useState('');
  const [userCount, setUserCount] = useState(1);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [groups, setGroups] = useState(['']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const monthlyTotal = (userCount || 1) * 5;

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        setError('Logo file size must be less than 2MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleAddGroup = () => {
    setGroups([...groups, '']);
  };

  const handleRemoveGroup = (index) => {
    setGroups(groups.filter((_, i) => i !== index));
  };

  const handleGroupChange = (index, value) => {
    const newGroups = [...groups];
    newGroups[index] = value;
    setGroups(newGroups);
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Ensure userCount is valid
    const validUserCount = Math.max(1, parseInt(userCount) || 1);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Not authenticated');

      // Create company document with auto-generated ID
      const companyRef = doc(db, 'companies', `company_${Date.now()}`);
      const companyId = companyRef.id;

      // Upload logo if provided
      let logoURL = null;
      if (logoFile) {
        const logoRef = ref(storage, `companies/${companyId}/logo`);
        await uploadBytes(logoRef, logoFile);
        logoURL = await getDownloadURL(logoRef);
      }

      // Filter out empty groups
      const validGroups = groups.filter(g => g.trim() !== '');

      // Use atomic batch write to prevent permission errors
      const batch = writeBatch(db);
      const userRef = doc(db, 'users', user.uid);
      const companyUserRef = doc(db, 'companies', companyId, 'users', user.uid);

      // 1️⃣ Create company
      batch.set(companyRef, {
        name: companyName,
        logoURL: logoURL,
        groups: validGroups,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        ownerId: user.uid,
        userLimit: validUserCount,
        subscriptionStatus: 'trial',
        subscriptionTier: 'professional',
        trialStartedAt: serverTimestamp(),
        trialEndsAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
      });

      // 2️⃣ Add owner as admin in company's users subcollection
      batch.set(companyUserRef, {
        email: user.email,
        role: 'admin',
        addedAt: serverTimestamp(),
      });

      // 3️⃣ Create or update user profile
      batch.set(
        userRef,
        {
          email: user.email,
          fullName: user.displayName || user.email.split('@')[0],
          companyId,
          role: 'admin',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // Commit all writes atomically before moving to the success screen.
      await batch.commit();

      navigate('/subscription/trial-success', { state: { userCount: validUserCount, companyId } });
    } catch (err) {
      console.error('Setup error:', err);
      setError(err.message || 'Failed to set up company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50 to-gray-200 flex flex-col items-center justify-center p-4">
      {/* Account creation progress */}
      <div className="mb-2">
        <ProgressStepper
          steps={[
            { label: 'Create Account' },
            { label: 'Company Setup' },
            { label: 'Start Trial' },
          ]}
          currentStep={1}
          variant="light"
        />
      </div>
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Set Up Your Company</h1>
        <p className="text-gray-600 mb-6">Configure your team size and subscription</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSetup} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Acme Corporation"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo (Optional)</label>
            <div className="flex items-center gap-4">
              {logoPreview && (
                <img src={logoPreview} alt="Logo preview" className="w-16 h-16 object-contain rounded border border-gray-300" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="flex-1 text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-red-700"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">PNG, JPG or SVG. Max 2MB.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Groups/Sites (Optional)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Organize users by location, department, or site (e.g., "London Office", "Manchester Site")
            </p>
            {groups.map((group, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={group}
                  onChange={(e) => handleGroupChange(index, e.target.value)}
                  placeholder={`Group ${index + 1}`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                />
                {groups.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveGroup(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={handleAddGroup}
              className="text-sm text-primary hover:text-red-700 font-medium"
            >
              + Add Another Group
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Users
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={userCount}
              onChange={(e) => {
                const value = parseInt(e.target.value);
                if (!isNaN(value) && value >= 1) {
                  setUserCount(value);
                } else if (e.target.value === '') {
                  // Allow empty input temporarily
                  setUserCount('');
                }
              }}
              onBlur={() => {
                // Enforce minimum on blur
                if (userCount === '' || userCount < 1) {
                  setUserCount(1);
                }
              }}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-sm text-gray-500 mt-1">£5 per user per month</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">Users:</span>
              <span className="font-semibold">{userCount || 1}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700">Price per user:</span>
              <span className="font-semibold">£5/month</span>
            </div>
            <div className="border-t border-gray-300 pt-2 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-lg font-bold text-gray-900">Monthly Total:</span>
                <span className="text-2xl font-bold text-primary">£{monthlyTotal}</span>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-red-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
          >
            {loading ? 'Setting up...' : 'Start Free Trial'}
          </button>
        </form>

        <p className="text-xs text-gray-500 mt-4 text-center">
          You'll be able to adjust your user count and subscription at any time
        </p>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------------------------
//  Build My Checks (c) 2025 Gareth Morgan - BuildMyChecks.co.uk
//  Solely designed, developed, and maintained by Gareth Morgan.
//  All intellectual property rights reserved.
//  No liability is accepted for unauthorised use, modification, deployment, or maintenance.
// ----------------------------------------------------------------------------------------------
